import type { CaseAssignmentFields } from "@/components/Forms/SubForms/CaseAssignmentModal.tsx";
import { createCaseSchema } from "@/components/Forms/schema/createCaseSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { createDataSelector, createResourceSelector, handleJsonApiError } from "jsonapi-zod-query";

const resourceSelector = createResourceSelector({
    type: "case",
    attributesSchema: createCaseSchema,
});

export const caseSelector = createDataSelector(resourceSelector);
export type NewCase = ReturnType<typeof caseSelector>;

export interface AddCaseParams {
    assignments?: CaseAssignmentFields;
}

export const useAddCase = (
    personUUID: string,
): UseMutationResult<NewCase, Error, AddCaseParams | null> => {
    const fetch = useAuthenticatedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params) => {
            const response = await fetch(apiUrl("/case/create"), {
                method: "PUT",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    personUUID: personUUID,
                    ...(params?.assignments ?? {}),
                }),
            });
            await handleJsonApiError(response);
            return await response.json();
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ["person-cases", personUUID],
            });
        },
    });
};
